<template>
    <div class="wp-block-almero-blocks-room-card container room-card" :class="{ 'featured-card': featured }">
        <div class="room-card__featured" v-if="data.featured">
            <span class="room-card__featured-star"></span> Featured Room
        </div>
        <div class="room-card__image-container">
            <Carousel :items="images" :alt="data.name" :overlay="true" class="room-card__gallery" ref="gallery">
                <div class="room-card__label" v-if="label" :style="{ color: label.color, backgroundColor: label.background }">
                    {{ decodeHTML(label.title) }}
                </div>
            </Carousel>
        </div>
        <div class="room-card__container">
            <div class="room-card__info-container">
                <div class="room-card__room-info">
                    <div class="room-card__top-data">
                        <div class="room-card__room-data">
                            <h3 class="room-card__room-title" v-html="data.name"></h3>
                            <p class="room-card__room-location">{{ decodeHTML(`${data.property}, ${data.location}`)}}</p>
                            <div class="room-card__room-type" v-if="data.featured">
                                <img :src="bedIcon" />
                                <span>{{ decodeHTML(data.type) }}</span>
                            </div>
                        </div>
                        <Price class="room-card__tarrif-details" :price="price" :selected-academic-year="selectedAcademicYear" />
                    </div>
                    <div class="room-card__body-data">
                        <div class="room-card__room-type" v-if="!data.featured">
                            <img :src="bedIcon" />
                            <span>{{ decodeHTML(data.type) }}</span>
                        </div>
                        <span class="room-card__excerpt" v-if="data.featured">{{data.excerpt}}</span>

                    </div>
                </div>
            </div>
            <div class="room-card__cta-container">
                <div class="room-card__cta room-card__cta--view wp-block-button is-style-secondary-button">
                    <a class="wp-block-button__link wp-element-button" :href="`${data.url}?academic_year_offset=${selectedYearOffset}`">See Room</a>
                </div>
                <div v-if="price === 0" class="room-card__cta room-card__cta--book wp-block-button is-style-primary-button">
                    <button type="button" class="wp-block-button__link wp-element-button openform" data-form="join_our_waiting_list" :data-form-id="data.id">Join our waiting list</button>
                </div>
                <div v-if="price > 0 && book_url" class="room-card__cta room-card__cta--book wp-block-button is-style-primary-button">
                    <a :href="book_url" target="_blank" class="wp-block-button__link wp-element-button">Book Now</a>
                </div>
                <div v-if="price > 0 && !book_url" class="room-card__cta room-card__cta--book wp-block-button is-style-primary-button">
                    <button type="button" class="wp-block-button__link wp-element-button openform" data-form="enquire_now" :data-form-id="data.id">Enquire now</button>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import Carousel from '@components/Carousel.vue';
import { themeDir } from '@utils/theme.js';

const bedIcon = `${themeDir}/images/icons/bed.svg`;

const props = defineProps({
    // The room data object (usually a search result)
    data: {
        type: Object,
        required: true
    },
    // The selected year offset, e.g. 0 for current year, 1 for next year
    selectedYearOffset: {
        type: Number,
        default: 1
    },
    // The current academic year range as a string, e.g. 2023/24
    currentAcademicYear: {
        type: String,
        required: true
    },
    // The next academic year range as a string, e.g. 2024/25
    nextAcademicYear: {
        type: String,
        required: true
    }
});

const gallery = ref(null);

// Get the 5 images to display in the gallery - if we have a main image, use
// that as the first image, followed by any other images. If we have no main
// image, use the other images (if any) or failing that, the placeholder image.
const images = computed(() => {
    const mainImage = props.data?.main_image;
    if (mainImage) {
        return (props.data.images.length ? [mainImage, ... props.data.images] : [mainImage]).slice(0, 5);
    }
    return imageUrl.value;
})

// Decode HTML entities without v-html - a bit safer as we only care about
// converting &amp; and &quot; etc.
const decodeHTML = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

// Which year was selected when computing the price
const selectedAcademicYear = ref("");
const featured = props.data.featured ? "featured-card" : "";

// Compute the price based on the selected year
const price = computed(() => {
    if (parseInt(props.selectedYearOffset, 10) === 0) {
        selectedAcademicYear.value = props.currentAcademicYear;
        return props.data.price_current;
    }
    if (parseInt(props.selectedYearOffset, 10) === 1) {
        selectedAcademicYear.value = props.nextAcademicYear;
        return props.data.price_next;
    }
    let priceCurrent = 0;
    if (props.selectedYearOffset < 1) {
        priceCurrent = props.data.price_current;
        selectedAcademicYear.value = props.currentAcademicYear;
    }
    else {
        priceCurrent = props.data.price_next;
        selectedAcademicYear.value = props.nextAcademicYear;
    }
    if (priceCurrent > 0) {
        return priceCurrent;
    }
    selectedAcademicYear.value = props.nextAcademicYear;
    return (props.data.price_next > 0) ? props.data.price_next : 0;
});

// Get the main image URL, or the placeholder if not found
const imageUrl = computed(() => {
    return props.data?.main_image ?? "/wp-content/themes/almerostudent/images/placeholder.png";
});

const SHOW_REMAINING_CONTRACTS = 3;

const LABELS = {
    "soldout": {
        title: "Sold Out",
        color: "var(--wp--preset--color--base)",
        background: "var(--wp--preset--color--vivid-red)"
    },
    "remaining": {
        title: "Only {remaining} Remaining",
        color: "var(--wp--preset--color--dark-blue)",
        background: "var(--wp--preset--color--cyan)"
    }
}

// Get the label object
const label = computed(() => {
    if (props.data.label?.title) {
        return props.data.label;
    }

    if (price.value <= 0) {
        return LABELS.soldout;
    }

    let remaining = 0;
    if (props.selectedYearOffset < 1) {
        remaining = props.data.rooms_current;
    }
    if (remaining <= 0 || props.selectedYearOffset >= 1) {
        remaining = props.data.rooms_next;
    }
    if (remaining > 0 && remaining <= SHOW_REMAINING_CONTRACTS) {
        return {
            title: LABELS.remaining.title.replace("{remaining}", remaining),
            color: LABELS.remaining.color,
            background: LABELS.remaining.background
        };
    }
});

// Get the booking URL
const book_url = computed(() => {
    if (selectedAcademicYear.value == props.currentAcademicYear) {
        return props.data.book_url_current ?? "";
    }
    return props.data.book_url_next ?? "";
});

</script>